//source/app.tsx
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./app.css";
import { fetchLeagueLeaderboard } from "./api";
import Loading from "./Loading";
import Header from "./Header";
import LeagueTable from "./LeagueTable";
import LEAGUES, { League } from "./constants/leagues";
import { Team } from "./types/team";
import { deserializeLeaderboard } from "./utils/deserializeLeaderboard";
import { useSwipe } from "./hooks/useSwipe";

const App = () => {
  const [loading, setLoading] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<League>(LEAGUES[0]);
  const [leagueData, setLeagueData] = useState<Record<string, Team[]>>({});

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const campaignGuid = params.get("campaignGuid");

  useEffect(() => {
    if (campaignGuid) {
      handleLoadLeague(campaignGuid);
    }
  }, [campaignGuid]);

  const handleLoadLeague = async (campaign: string) => {
    setLoading(true);

    try {
      // Loading first league
      let nextPageToken = await loadLeague(campaign, LEAGUES[0].key);

      // Show UI
      setLoading(false);

      // Prefetching remaining leagues
      for (const league of LEAGUES.slice(1)) {
        nextPageToken = await loadLeague(campaign, league.key, nextPageToken);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeague = async (
    campaign: string,
    key: League["key"],
    token?: string,
  ) => {
    const data = await fetchLeagueLeaderboard({ campaign }, token);

    setLeagueData((prev) => ({
      ...prev,
      [key]: (data.results ?? []).map(deserializeLeaderboard),
    }));

    return data.next;
  };

  const currentIndex = LEAGUES.findIndex(
    (league) => league.key === selectedLeague.key,
  );

  const swipeHandlers = useSwipe({
    onSwipeLeft: () => {
      if (currentIndex < LEAGUES.length - 1) {
        setSelectedLeague(LEAGUES[currentIndex + 1]);
      }
    },
    onSwipeRight: () => {
      if (currentIndex > 0) {
        setSelectedLeague(LEAGUES[currentIndex - 1]);
      }
    },
  });

  if (loading) {
    return (
      <div className="loading-container">
        <Loading size={2} />
      </div>
    );
  }

  const teams = leagueData[selectedLeague.key] ?? [];

  return (
    <div {...swipeHandlers}>
      <Header
        navigation={LEAGUES}
        selected={selectedLeague.key}
        onSelect={(key) => {
          const league = LEAGUES.find((league) => league.key === key);
          if (league) {
            setSelectedLeague(league);
          }
        }}
      />
      <LeagueTable
        league={selectedLeague}
        teams={teams}
      />
    </div>
  );
};

const container = document.getElementById("app");

if (!container) {
  throw new Error('Root element with id "app" not found.');
}

const root = ReactDOM.createRoot(container);

root.render(<App />);
